import { Event } from './Event';
import * as  firebaseKeyCodec from './firebaseKeyCodec';
import { enterFunction } from './listenerWrappers/enterFunction';
import redundancyRemover from './listenerWrappers/redundancyRemover';
import * as gateway from '../eventsManager/gateway';

var wrappers = {
    enterFunction: enterFunction,
    redundancyRemover: redundancyRemover
};

var wrappedListener = function (wrappers, listener) {
    return wrappers.reduce(function (acc, curr) {
        return curr(acc);
    }, listener);
};

/**
 * Safe lookup a deep path within an object
 * @param path
 * @param obj
 * @returns {*}
 */
var resolvePath = function (path, obj) {
    var _resolvePathSegment = function (pathSegment, obj) {
        if (typeof obj === "undefined" || obj === null || typeof obj !== "object") {
            return null;
        }

        if (!(pathSegment in obj)) {
            return null;
        }

        return obj[pathSegment];
    };

    return (path || []).reduce(function (acc, curr) {
        return _resolvePathSegment(curr, acc);
    }, obj);
};

interface Connection {
    isConnectionType(type: string): boolean;
    isCurrent(): boolean;
    connect(): void;
    disconnect(): void;
}

/**
 * A pairing between a listener and the firebase.
 * @param listener
 * @param type
 * @param path
 * @constructor
 */

class FirebaseConnection implements Connection {
    private listener: ListenerShell;
    private type: string;
    private path: Array<string>;
    private firebase;
    private resolvedFirebase;
    private firebaseCallbacks = {};

    constructor(listener: ListenerShell, type: string, path: Array<string>) {
        this.listener = listener;
        this.type = type;
        this.path = path;
        this.firebase = this.listener.getFirebaseFromEvent();
        this.resolvedFirebase = this.resolveFirebase();
    }

    public isConnectionType(type: string) {
        return type === "firebase";
    };

    public isCurrent() {
        return this.firebase === this.listener.getFirebaseFromEvent();
    }

    private resolveFirebase() {
        //eleminate the "database" prefix from the path
        let pathToUse = this.path.slice(1);
        let fb = pathToUse.reduce((acc, curr) => {
            return acc.child(curr);
        }, this.firebase);
        return fb;
    }

    private registerFirebaseCallback(fbCallbackType, fbCallback) {
        const fb = this.resolvedFirebase;
        const callback = fb.on(
            fbCallbackType,
            fbCallback,
            (err) => {
                let onError = this.listener.getListenerObj().onError;
                if (onError) {
                    onError(err);
                }
                else {
                    var t = this.listener.getTelemetry();
                    if (err.code == "PERMISSION_DENIED") {
                        t.debug("Callback cancelled because of permissions");
                    }
                    else {
                        t.error("Error from firebase, callback is cancelled");
                        t.error(err);
                    }
                }
                delete this.firebaseCallbacks[fbCallbackType];
                //Let the listener know that we have stopped
                if (this.listener.isConnected()) {
                    // NOT SURE WHAT THIS IS FOR
                    // this.listener.onConnectionLost(err);
                    this.listener.onConnectionLost();
                }
            }
        );
        this.firebaseCallbacks[fbCallbackType] = callback;
    }

    private connectWith_value() {
        this.registerFirebaseCallback("value", (snapshot) => {
            setImmediate(() => {
                const onValue = this.listener.getListenerObj().onValue;
                onValue(firebaseKeyCodec.decode(snapshot.val()));
            });
        });
    
        this.listener.getTelemetry().debug("now connected to firebase");
    };

    private connectWith_set() {
        this.registerFirebaseCallback("child_added", (snapshot) => {
            setImmediate(() => {
                const f = this.listener.getListenerObj().onChildAdded;
                f(firebaseKeyCodec.decode(snapshot.key), firebaseKeyCodec.decode(snapshot.val()));
            });
        });
    
        this.registerFirebaseCallback("child_changed", (snapshot) => {
            setImmediate(() => {
                const f = this.listener.getListenerObj().onChildChanged;
                f(firebaseKeyCodec.decode(snapshot.key), firebaseKeyCodec.decode(snapshot.val()));
            });
        });
    
        this.registerFirebaseCallback("child_removed", (snapshot) => {
            setImmediate(() => {
                const f = this.listener.getListenerObj().onChildRemoved;
                f(firebaseKeyCodec.decode(snapshot.key), firebaseKeyCodec.decode(snapshot.val()));
            });
        });
    
        this.listener.getTelemetry().debug("now connected to firebase");
    };

    public connect() {
        this["connectWith_" + this.type].apply(this, arguments);
        const onConnected = this.listener.getListenerObj().onConnected;
        if (onConnected) {
            onConnected();
        }
    };
    
    public disconnect() {
        const fb = this.resolvedFirebase;
        Object.keys(this.firebaseCallbacks).forEach((key) => {
            fb.off(key, this.firebaseCallbacks[key]);
        });
    
        this.listener.onConnectionLost();
    };
}

/**
 * A pairing between a listener and the publicInfo.
 * @param listener
 * @param type
 * @param path
 * @constructor
 */

 class PublicInfoConnection implements Connection {
    
    private listener: ListenerShell;
    private type: string;
    private path: Array<string>;
    private publicInfo;

    constructor(listener: ListenerShell, type: string, path: Array<string>) {
         this.listener = listener;
         this.type = type;
         this.path = path;
         this.publicInfo = this.listener.getCachedPublicInfoFromEvent();
     }

     public isCurrent() {
         return this.publicInfo === this.listener.getCachedPublicInfoFromEvent();
     }

     public isConnectionType(type: string) {
        return type === 'publicInfo';
     }

     private connectWith_value() {
         const node = resolvePath(this.path, this.publicInfo);
         this.listener.getTelemetry().debug("now connected to publicInfo");
         const onValue = this.listener.getListenerObj().onValue;
         onValue(node);
     }

     private connectWith_set() {
        const node = resolvePath(this.path, this.publicInfo);
        this.listener.getTelemetry().debug("now connected to publicInfo");
        const onChildAdded = this.listener.getListenerObj().onChildAdded;
        if (node === null) {
            return;
        }
        Object.keys(node).forEach((key) => {
            onChildAdded(this.listener, key, node[key]);
        })
     }

    public connect() {
        this["_connectWith_" + this.type].apply(this, arguments);
        const onConnected = this.listener.getListenerObj().onConnected;
        if (onConnected) {  
            onConnected();
        }
    };

    public disconnect() {
        // THIS SEEMS WRONG
        // This._onConnectionLost();
        this.listener.onConnectionLost();
    };
 }


const getConnectionClass = (expectedSource) => {
    if (expectedSource == "firebase") {
        return FirebaseConnection;
    }
    else if (expectedSource == "publicInfo") {
        return PublicInfoConnection;
    }
};

export interface IListenerShell {
    detach(): void;
    isConnected(): boolean;
    getTelemetry(): any;
    getFirebaseFromEvent()
    getCachedPublicInfoFromEvent()
    getListenerObj()
    isDatabaseListener() 
    ensureConnection()
    onConnectionLost()
    disconnect(): void;
}

/**
 * Wraps around a listener object.
 * Provides helpful functions to Event and DataSource classes to operate on listeners
 * @param type
 * @param path
 * @param listenerObj
 * @constructor
 */

export class ListenerShell implements IListenerShell {

    private event: Event;
    private dataSource;
    private telemetry;
    private path;
    private listenerType;
    private listenerObj;
    private debug: boolean = false;
    private currentConnection = null;
    private doneOnce: boolean = false;

    constructor(event: Event, dataSource, path: Array<string>, type: string, listenerObj) {
        this.event = event;
        this.dataSource = dataSource;
        this.telemetry = dataSource.getTelemetry().clone();
        this.telemetry.context().path = path.join('/');
        this.path = path.map(firebaseKeyCodec.encode);
        this.listenerType = type;
        this.listenerObj = wrappedListener([
            wrappers.enterFunction(dataSource._enterFunction),
            wrappers.redundancyRemover
        ], listenerObj);
        this.getTelemetry().debug('attached');
        this.dataSource._listeners.push(this);
    }

    public detach() {
        this.disconnect();
        let listenerIndex = this.dataSource._listeners.indexOf(this);
        if (listenerIndex < 0) {
            return;
        }
        this.getTelemetry().debug('detached');
        this.dataSource._listeners.splice(listenerIndex, 1);
    }

    public isConnected() {
        return !!this.currentConnection;
    }

    public getTelemetry() {
        let unitFunction = () => {};
        if (this.debug) {
            return this.telemetry;
        }
    
        return {
            clone: this.telemetry.clone.bind(this.telemetry),
            trace: unitFunction,
            debug: unitFunction,
            info: this.telemetry.info.bind(this.telemetry),
            warn: this.telemetry.warn.bind(this.telemetry),
            error: this.telemetry.error.bind(this.telemetry),
        };
    }

    public getFirebaseFromEvent() {
        return this.event.getFirebase();
    }

    public getCachedPublicInfoFromEvent() {
        return this.event.getCachedPublicInfo();
    }

    public getListenerObj() {
        return this.listenerObj;
    }

    public isDatabaseListener() {
        return this.path[0] == "database";
    }

    public isGatewayListener() {
        return this.path[0] == "gateway";
    }

    public ensureConnection() {
        if (this.isDatabaseListener()) {
            this.ensureConnection_database();
            return;
        } else if (this.isGatewayListener()) {
            (gateway.executeNamedQuery as any)(this.dataSource, this.path[1])
                .then(val => this.listenerObj.onValue(val));
            return;
        }
        this.ensureConnection_nonDatabase();
    };
    
    public onConnectionLost() {
        this.currentConnection = null;
    };

    private ensureConnection_database() {

        let expectedSource = this.event.getExpectedDatabaseSource();
    
        if (!expectedSource && !this.isConnected()) {
            this.getTelemetry().debug("not connected");
            return;
        }
    
        if (this.isConnected() &&
            this.currentConnection.isConnectionType(expectedSource) &&
            this.currentConnection.isCurrent()) {
    
    
            this.getTelemetry().debug("already connected to " + expectedSource);
            return;
        }
    
        this.disconnect();
    
        let Connection = getConnectionClass(expectedSource);
        this.currentConnection = new Connection(this, this.listenerType, this.path);
        this.currentConnection.connect();
    };
    
    private ensureConnection_nonDatabase() {
        let path = this.path[0];
        if (path == "currentPersonId") {
            this.event.getCurrentPersonId().then((personId) => {
                this.listenerObj.onValue(personId);
            });
            return;
        }
    
        let info = this.event.getEventsManager().getEventInfoCache().get(this.event.getEventId());
        let publicInfo = this.event.getEventsManager().getEventPublicInfoCache().get(this.event.getEventId());
    
        if(info) {
            if(path == "eventAcl_role" && info.eventAcl_role) {
                this.listenerObj.onValue(info.eventAcl_role);
            }
            else if(path == "assetConnection" && info.assetConnection) {
                this.listenerObj.onValue(info.assetConnection);
            }
            else if(path == "assetStores") {
                this.event.getEventAssetStores()
                    .then(function(assetStores) {
                        this.listenerObj.onValue(assetStores);
                    });
            }
            else if (path == "serviceApiEventUserProfiles" && !this.doneOnce) {
                this.doneOnce = true;
                this.event.getServiceApiEventUserProfiles(this.path[1]).then((profiles) => {
                    this.listenerObj.onValue(profiles);
                });
            }
            return;
        }
    
        if(publicInfo) {
            if(path == "eventAcl_role" && publicInfo.eventAcl_role) {
                this.listenerObj.onValue(publicInfo.eventAcl_role);
            }
            else if(path == "assetConnection" && publicInfo.assetConnection) {
                this.listenerObj.onValue(publicInfo.assetConnection);
            }
            return;
        }
        this.getTelemetry().debug("not connected");
        return;
    };
    
    public disconnect() {
        if (!this.isConnected()) {
            return;
        }
    
        this.currentConnection.disconnect();
        this.currentConnection = null;
    };
    
}
